import {
  CdkDrag,
  CdkDragDrop,
  CdkDragSortEvent,
  CdkDropListGroup,
  ɵɵCdkScrollable,
  CdkDropList,
  CdkDragPlaceholder,
  CdkDragPreview,
} from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, Renderer2, inject, viewChild } from '@angular/core';
import { SpinnerService } from 'apps/cmg-clear2-ui/src/app/shared/services/spinner.service';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { EMPTY, Subject, catchError, finalize, take } from 'rxjs';
import { Cmg1003Service } from '../../../services/cmg-1003.service';
import { CmgUiUtilities } from '../../common/cmg-ui-utils';
import { ManageBorrowersDeleteConfirmationModalComponent } from '../manage-borrowers-delete-confirmation/manage-borrowers-delete-confirmation.component';
import { ManageApplicationModel, ManageBorrowerModel, SwitchBorrowerModel } from './manage-borrower.model';
import { addIconClassToModalHeader } from 'apps/cmg-clear2-ui/src/app/shared';
import { ButtonDirective, ButtonIcon, Button } from 'primeng/button';
import { Message } from 'primeng/message';
import { NgClass } from '@angular/common';

@Component({
  selector: 'cmg-clear2-ui-manage-borrowers-modal',
  templateUrl: './manage-borrowers-modal.component.html',
  styleUrls: ['./manage-borrowers-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonDirective,
    ButtonIcon,
    CdkDropListGroup,
    Message,
    ɵɵCdkScrollable,
    CdkDropList,
    CdkDrag,
    NgClass,
    CdkDragPlaceholder,
    CdkDragPreview,
    Button,
  ],
})
export class ManageBorrowersModalComponent implements OnInit, OnDestroy {
  private readonly _dialogRef = inject(DynamicDialogRef);
  private readonly _dynamicDialogConfig = inject(DynamicDialogConfig);
  private readonly _dialogService = inject(DialogService);
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _messageService = inject(MessageService);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _renderer = inject(Renderer2);
  private readonly _cmgService = inject(Cmg1003Service);

  readonly dragContainerRef = viewChild<ElementRef>('dragContainer');
  readonly messageBar = viewChild<ElementRef>('messageBar');
  applications: ManageApplicationModel[];
  borrowers: ManageBorrowerModel[];

  errorMessages: ToastMessageOptions[];
  readonly deletedContainerId = -1;
  get deletedBorrowers(): any[] {
    return this.borrowers?.filter((t) => !t.appId);
  }

  get activeApplications(): any[] {
    return this.applications?.filter((t) => t.isDeleted === false);
  }

  private readonly _destroyed$ = new Subject<boolean>();
  private readonly _catchManageBorrowersError = () => {
    // show the message and return empty - this will keep the old state in store.
    this._messageService.add({ severity: 'error', summary: 'Manage Borrowers', detail: 'Unable to update Borrowers; please try again.' });
    return EMPTY;
  };

  private readonly _isFirstDropPosition = (index: number) => index === 0;

  ngOnInit(): void {
    this.applications = this._dynamicDialogConfig.data.applications;
    this.borrowers = this._dynamicDialogConfig.data.borrowers;
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  accept() {
    const deleteBorrowers = this.borrowers.filter((t) => t.appId === null);
    if (deleteBorrowers?.length > 0) {
      const deleteDialogRef = this._dialogService.open(ManageBorrowersDeleteConfirmationModalComponent, {
        width: '450px', // should be smaller than manage-borrowers-modal
        closable: true,
        dismissableMask: false,
        position: 'center',
        header: 'Delete Borrower(s)',
        keepInViewport: true,
        styleClass: 'manage-borrowers-modal',
        data: [...deleteBorrowers.map((t) => t.name)],
      });
      addIconClassToModalHeader('pi pi-info-circle');

      deleteDialogRef.onClose.subscribe((response) => {
        if (response) {
          setTimeout(() => {
            this._dialogRef.close();
          });
          this._cmgService
            .manageBorrowers({
              borrowers: this.borrowers,
              applications: this.applications,
            })
            .pipe(
              take(1),
              catchError(this._catchManageBorrowersError),
              finalize(() => {
                this._spinnerService.hide();
              }),
            )
            .subscribe(() => {
              this._dialogRef.close();
              this._messageService.add({ severity: 'success', summary: '', detail: 'Data saved successfully!' });
              this._cmgService.reset();
            });
        }
      });
    } else {
      setTimeout(() => {
        this._dialogRef.close();
      });
      if (this.borrowers.length > 1 || this.applications.length > 1) {
        this._spinnerService.show('Saving loan...');
        this._cmgService
          .manageBorrowers({
            borrowers: this.borrowers,
            applications: this.applications,
          })
          .pipe(
            take(1),
            catchError(this._catchManageBorrowersError),
            finalize(() => {
              this._spinnerService.hide();
            }),
          )
          .subscribe(() => {
            this._messageService.add({ severity: 'success', summary: '', detail: 'Data saved successfully!' });
            this._cmgService.reset();
          });
      }
    }
  }

  reject() {
    this._dialogRef.close();
  }

  /**
   * Only applies to Application #1
   * Swap co-borrower and primary borrower roles.
   * @param borrowerId
   */
  makePrimary(borrowerId: number) {
    this.borrowers.forEach((borrower) => {
      if (borrower.isPrimary && borrower.id !== borrowerId) {
        borrower.isPrimary = false;
        borrower.displayOrder = 1;
      }

      if (borrower.id === borrowerId) {
        borrower.isPrimary = true;
        borrower.displayOrder = 0;
      }
    });
  }

  borrowersByApp(appId: number) {
    return this.borrowers.filter((t) => t.appId === appId).sort((a, b) => a.displayOrder - b.displayOrder);
  }

  /**
   * Called when a borrower is dropped from another application/deleted container in
   * to a different/same application
   * @param $event
   */
  onBorrowerDroppedInApplication($event: CdkDragDrop<ManageBorrowerModel>) {
    const previousAppId = +$event.previousContainer.data;
    const currentAppId = +$event.container.data;
    const prevBorrowerIndex = $event.previousIndex;
    const currentBorrowerIndex = $event.currentIndex;
    const borrower = $event.item.data as ManageBorrowerModel;
    if (previousAppId !== currentAppId) {
      // borrower moved
      if (currentAppId === this.deletedContainerId) {
        // from application to deleted bucket
        borrower.appId = null;
      } else {
        // if the borrower somehow lands before Primary borrower then disallow that drop.
        if (currentBorrowerIndex === 0 && this._isApplicationPrimary(currentAppId)) {
          return;
        }
        // check to see how many borrowers are in here.
        const borrowersInApp = this.borrowers.filter((t) => t.appId === currentAppId).length;
        if (borrowersInApp > 1) {
          this._swapAcrossApp(borrower, currentAppId, currentBorrowerIndex, previousAppId, prevBorrowerIndex);
        } else {
          this._setBorrowerPosition(borrower, currentAppId, currentBorrowerIndex);
        }
      }
    } else {
      // move the item in array
      if (!this.borrowers.some((t) => t.appId === currentAppId && t.isPrimary === true)) {
        this._swap(this.borrowers.filter((t) => t.appId === currentAppId));
      }
    }
  }

  /**
   * Called when a borrower is dropped in 'New Application Drop zone'
   * @param $event
   */
  createNewApplicationWithBorrower($event: CdkDragDrop<ManageBorrowerModel>) {
    // $event.item.data is the borrower model
    const previousAppId = +$event.previousContainer.data;
    const previousApp = this.applications.find((t) => t.id === previousAppId);
    const previousAppBorrowerCount = this.borrowers.filter((t) => t.appId === previousAppId).length;
    const borrower = <ManageBorrowerModel>$event.item.data;
    // if the borrower is from the last application, and is the only one, then don't allow dropping.
    if (previousAppBorrowerCount === 1 && previousApp.displayOrder === this.applications.length - 1) {
      // show message
      this.errorMessages = [{ severity: 'error', detail: `Unable to move ${borrower.name} to new application.` }];
      // bring it in to view
      setTimeout(() => {
        this.messageBar()?.nativeElement?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'end' });
      });
    } else {
      const newApp = <ManageApplicationModel>{};
      newApp.displayOrder = this.applications.length;
      newApp.isDeleted = false;
      newApp.id = -CmgUiUtilities.getUniqueNumber();

      $event.item.data.appId = newApp.id;
      $event.item.data.displayOrder = 0; // will always be zero b/c new app is getting created.
      this.applications.push(newApp);
      this._cleanUpApplications();
      this._changeDetectorRef.detectChanges();
    }
  }

  onBorrowerDragStarted(): void {
    this.errorMessages = null; // clear any previous error messages b/c new dragging started.
    this._renderer.addClass(document.body, 'cursor-pointer');
    this._changeDetectorRef.detectChanges();
  }

  onBorrowerEnteredApplication($event: CdkDragDrop<ManageBorrowerModel>) {
    const appId = +$event.container.data;
    const draggedborrowerId = +$event.item.data.id;
    const currentBorrowerDropIndex = $event.currentIndex;
    const isSwitchingBorrowers = this._isSwitchingBorrower(appId, draggedborrowerId);
    const isDragDropNotAllowed = this._isFirstDropPosition(currentBorrowerDropIndex) && this._isApplicationPrimary(appId);
    const switchInfo: SwitchBorrowerModel = {
      isSwitching: isSwitchingBorrowers,
      previewElement: $event?.item?._dragRef['_preview'] as HTMLElement,
      draggedBorrowerName: $event?.item?.data?.name,
      switchedBorrowerName: null,
      isSwitchedAllowed: true,
    };

    if (isSwitchingBorrowers) {
      const cdkDragItemsInContainer = Array.from($event.container['_unsortedItems']);
      const borrowerItemsInContainer = [];
      cdkDragItemsInContainer.forEach((item: CdkDrag) => {
        borrowerItemsInContainer.push(item.data.name);
      });

      switchInfo.switchedBorrowerName = this._isFirstDropPosition(currentBorrowerDropIndex)
        ? borrowerItemsInContainer[0]
        : borrowerItemsInContainer[1];
      switchInfo.isSwitchedAllowed = !isDragDropNotAllowed;
    }
    this._switchingBorrower(switchInfo);
  }

  onBorrowerMovingIntoPosition($event: CdkDragSortEvent<ManageBorrowerModel>) {
    const appId = +$event.container.data;
    const draggedborrowerId = +$event.item.data.id;
    const currentBorrowerDropIndex = $event.currentIndex;
    const isSwitchingBorrowers = this._isSwitchingBorrower(appId, draggedborrowerId);

    const dragPlaceholder = $event.item.getPlaceholderElement();
    const isDragDropNotAllowed = this._isFirstDropPosition(currentBorrowerDropIndex) && this._isApplicationPrimary(appId);
    isDragDropNotAllowed ? dragPlaceholder.classList.add('not-allowed') : dragPlaceholder.classList.remove('not-allowed');
    const switchInfo: SwitchBorrowerModel = {
      isSwitching: isSwitchingBorrowers,
      previewElement: $event?.item?._dragRef['_preview'] as HTMLElement,
      draggedBorrowerName: $event?.item?.data?.name,
      switchedBorrowerName: null,
      isSwitchedAllowed: true,
    };
    if (isSwitchingBorrowers) {
      const cdkDragItemsInContainer = Array.from($event.container['_unsortedItems']);
      const borrowerItemsInContainer = [];
      cdkDragItemsInContainer.forEach((item: CdkDrag) => {
        borrowerItemsInContainer.push(item.data.name);
      });

      switchInfo.switchedBorrowerName = this._isFirstDropPosition(currentBorrowerDropIndex)
        ? borrowerItemsInContainer[0]
        : borrowerItemsInContainer[1];
      switchInfo.isSwitchedAllowed = !isDragDropNotAllowed;
    }

    this._switchingBorrower(switchInfo);
  }

  onBorrowerExitedApplication($event: CdkDragDrop<ManageBorrowerModel>) {
    const switchInfo: SwitchBorrowerModel = {
      isSwitching: false,
      previewElement: $event?.item?._dragRef['_preview'] as HTMLElement,
      draggedBorrowerName: $event?.item?.data?.name,
      switchedBorrowerName: null,
      isSwitchedAllowed: true,
    };

    this._switchingBorrower(switchInfo);
  }

  onBorrowerDragDropped(/* $event: any */) {
    /** Intentionally left empty because it is very helpful in debugging. */
  }

  onBorrowerDragEntered(/* $event: any */) {
    /** Intentionally left empty because it is very helpful in debugging. */
  }

  onBorrowerDragExited(/* $event: any */) {
    /** Intentionally left empty because it is very helpful in debugging. */
  }

  onBorrowerDragReleased(/* $event: any */) {
    /** Intentionally left empty because it is very helpful in debugging. */
  }

  onBorrowerDragEnded() {
    this._renderer.removeClass(document.body, 'cursor-pointer');
    this._changeDetectorRef.detectChanges();
  }

  deleteBorrower($event: Event, borrowerId) {
    $event.stopImmediatePropagation();
    $event.stopPropagation();
    $event.preventDefault();
    const deletedBorrower = this.borrowers.find((t) => t.id === borrowerId);
    if (deletedBorrower) {
      deletedBorrower.appId = null;
      this._cleanUpApplications();
    }
  }

  /**
   * Checks to  see if the passed appId is the primary borrowers app id.
   * @param appId
   */
  private _isApplicationPrimary(appId: number) {
    return this.borrowers.some((t) => t.isPrimary === true && t.appId === appId);
  }

  private _cleanUpApplications(): any {
    this.applications.forEach((a) => {
      // if only 1 borrower in app - make the display order 0
      if (this.borrowers.filter((b) => b.appId === a.id)?.length === 1) {
        this.borrowers.filter((b) => b.appId === a.id)[0].displayOrder = 0;
      }

      if (this.borrowers.some((b) => b.appId === a.id) === false) {
        a.isDeleted = true;
      }
    });
  }

  private _swap(borrowers: ManageBorrowerModel[]) {
    if (borrowers && borrowers.length === 2) {
      const tempDisplayOrder = borrowers[0].displayOrder;
      borrowers[0].displayOrder = borrowers[1].displayOrder;
      borrowers[1].displayOrder = tempDisplayOrder;
      if (borrowers[0].isPrimary === true || borrowers[1].isPrimary === true) {
        const tempIsPrimary = borrowers[0].isPrimary;
        borrowers[0].isPrimary = borrowers[1].isPrimary;
        borrowers[1].isPrimary = tempIsPrimary;
      }
    }
    this._cleanUpApplications();
  }

  private _swapAcrossApp(
    borrower: ManageBorrowerModel,
    currentAppId: number,
    currentBorrowerIndex: number,
    previousAppId: number,
    previousDislpayOrder: number,
  ) {
    const displayOrder = this._isFirstDropPosition(currentBorrowerIndex) ? 0 : 1;
    const borrowerToMove = this.borrowers.find((b) => b.appId === currentAppId && b.displayOrder === displayOrder);
    if (borrowerToMove && borrowerToMove.isPrimary === false) {
      borrowerToMove.appId = previousAppId;
      borrowerToMove.displayOrder = previousDislpayOrder;
      borrower.appId = currentAppId;
      borrower.displayOrder = displayOrder;
    }
    this._cleanUpApplications();
  }

  /**
   * Moves the borrower in the app and sets the current index
   * @param borrower
   * @param currentAppId
   * @param currentIndex
   */
  private _setBorrowerPosition(borrower: ManageBorrowerModel, currentAppId: number, displayOrder: number) {
    // find id there is a borrower with same displayOrder in this app
    const borrowerToMove = this.borrowers.find((b) => b.appId === currentAppId && b.displayOrder === displayOrder);
    if (borrowerToMove) {
      borrower.displayOrder = displayOrder;
      borrower.appId = currentAppId;
      borrowerToMove.displayOrder = displayOrder === 0 ? 1 : 0;
    } else {
      borrower.displayOrder = 1;
      borrower.appId = currentAppId;
    }
    this._cleanUpApplications();
  }

  private _switchingBorrower(switchInfoRef: SwitchBorrowerModel): void {
    const switchElementRef = switchInfoRef?.previewElement?.querySelector('#switchText');
    const borrowerTextElementRef = switchInfoRef?.previewElement?.querySelector('#borrowerName');
    if (switchElementRef && borrowerTextElementRef) {
      if (switchInfoRef.isSwitching) {
        this._removeAllChildNodes(switchElementRef);
        this._removeAllChildNodes(borrowerTextElementRef);
        const txtNodeSwitch = document.createElement('span');
        if (switchInfoRef.isSwitchedAllowed) {
          txtNodeSwitch.innerText = 'SWITCH';
          const txtBorrowerSwitch = document.createElement('span');
          txtBorrowerSwitch.innerText = `${switchInfoRef.draggedBorrowerName} with ${switchInfoRef.switchedBorrowerName}`;
          txtBorrowerSwitch.classList.add('ms-3');
          switchElementRef.appendChild(txtNodeSwitch);
          borrowerTextElementRef.appendChild(txtBorrowerSwitch);
        } else {
          const txtBorrowerSwitch = document.createElement('span');
          txtBorrowerSwitch.innerText = `${switchInfoRef.draggedBorrowerName}`;
          txtBorrowerSwitch.classList.add('ms-3');
          borrowerTextElementRef.appendChild(txtBorrowerSwitch);
        }
      } else {
        this._removeAllChildNodes(switchElementRef);
        this._removeAllChildNodes(borrowerTextElementRef);
        const txtBorrowerSwitch = document.createElement('span');
        txtBorrowerSwitch.innerText = `${switchInfoRef.draggedBorrowerName}`;
        txtBorrowerSwitch.classList.add('ms-3');
        borrowerTextElementRef.appendChild(txtBorrowerSwitch);
      }
      this._changeDetectorRef.detectChanges();
    }
  }

  private _removeAllChildNodes(switchElementRef: Element) {
    while (switchElementRef.firstChild) {
      switchElementRef.removeChild(switchElementRef.firstChild);
    }
  }

  private _isSwitchingBorrower(appId: number, draggedborrowerId: number) {
    const borrowersInTheDestinationContainer = this.borrowers.filter((t) => t.appId === appId);
    return (
      appId !== this.deletedContainerId &&
      borrowersInTheDestinationContainer?.length === 2 &&
      !borrowersInTheDestinationContainer.some((t) => t.id === draggedborrowerId)
    );
  }
}
